<template>
  <div>
    <modal
      data-e2e-attr="verify-otp-popup"
      :displayModal="otpModal"
      :displayShade="otpShade"
      @hide-all="handleReset"
      @show-modal="otpModal = true"
    >
      <div class="text-center">
        <h2
          class="text-2xl font-bold leading-9 tracking-tight text-center tmsk-blue-text"
        >
          Enter OTP
        </h2>

        <p
          class="max-w-2xl mx-auto text-base leading-7 text-center tmsk-blue-text"
        >
          Key in the
          <span class="font-bold">One Time Password (OTP)</span>
          sent to your phone.
        </p>
      </div>
      <div class="grid max-w-xl gap-8 mx-auto mt-2">
        <div class="relative rounded-xl">
          <div class="relative mt-1 rounded-xl">
            <input
              id="otp"
              autocomplete="one-time-code"
              type="tel"
              class="block w-full py-2 pl-6 pr-10 placeholder-gray-300 border rounded-full outline-none tmsk-blue-border sm:text-sm sm:leading-5"
              :placeholder="$t('verification.otp.input.placeholder')"
              v-model="otp"
              @keyup.enter="handleSubmit"
            />
            <div
              v-if="valid"
              class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
            >
              <svg
                class="w-5 h-5 text-green-500"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
          </div>

          <p v-if="valid === false" class="mt-2 text-sm text-red-600" data-test="invalid-otp-error">
            You have entered an invalid OTP. Please try again.
          </p>
          <p class="mt-2 text-base text-center tmsk-blue-text">
            Did not receive an SMS?
            <span
              :class="
                resendTimer > 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'cursor-pointer'
              "
              class="underline"
              data-e2e-attr="resend-otp-btn"
              @click="handleResendOTP"
            >
              Resend OTP</span
            >.
          </p>
          <div class="flex flex-wrap mt-2 sm:mt-6 sm:gap-3-flow-row-dense">
            <span class="flex w-full px-2 mt-2 rounded-full shadow-sm md:w-1/2">
              <button
                data-e2e-attr="clear-otp-btn"
                type="button"
                class="inline-flex justify-center w-full px-4 py-2 text-base font-medium leading-6 text-white border border-transparent rounded-full tmsk-blue-bg focus:outline-none sm:text-sm sm:leading-5"
                @click="handleClear"
              >
                Clear
              </button>
            </span>
            <span class="flex w-full px-2 mt-2 rounded-full shadow-sm md:w-1/2">
              <button
                data-e2e-attr="validate-otp-btn"
                type="button"
                class="inline-flex justify-center w-full px-4 py-2 text-base font-medium leading-6 text-white border border-transparent rounded-full tmsk-blue-bg focus:outline-none sm:text-sm sm:leading-5"
                @click="handleSubmit"
              >
                Submit
              </button>
            </span>
          </div>

          <div data-test="test-otp-message-container" v-if="testOTP" class="p-4 mt-4 rounded-md bg-blue-50">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg
                  class="w-5 h-5 text-blue-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              <div class="flex-1 ml-3 info-box md:flex md:justify-between">
                <p class="text-sm leading-5 text-blue-700">
                  Your OTP is
                  <span data-e2e-attr="otp-text"
                    >{{ resentOTP || testOTP }}</span
                  >.
                  This is only for testing purposes. Useful when you are testing
                  with a phone number that you do not have access to.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import beepAPI from '../../../mixins/beepAPI'
import { mapActions, mapGetters } from 'vuex'
import Modal from '../../../components/Modal'

export default {
  name: 'VerifyOtpModal',
  components: {
    Modal
  },
  mixins: [beepAPI],
  props: {
    recipient: {
      type: String,
      required: true
    },
    testOTP: {
      type: Number,
      default: null
    },
    hashPass: {
      type: String,
      required: true
    },
    fromName: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      otpModal: false,
      otpShade: false,
      resendTimer: null
    }
  },
  computed: {
    ...mapGetters(['otp', 'valid', 'resentOTP', 'token', 'mobileNumberStates']),
    otp: {
      get () {
        return this.$store.state.verifyOtpModal.otp
      },
      set (value) {
        this.$store.commit('setOtp', value)
      }
    }
  },
  watch: {
    resendTimer (newValue) {
      if (newValue > 0) {
        setTimeout(() => {
          this.resendTimer -= 1
        }, 1000)
      } else if (newValue === 0) {
        this.resendTimer = null
      }
    }
  },
  mounted () {
    this.resetResendOtpTimer()
    this.otpShade = true
  },
  methods: {
    ...mapActions([
      'resetOtpValidity',
      'clearOtpInput',
      'verifyOtp',
      'resendOTP',
      'updateIsLoggedIn',
      'getUserMobileNumber'
    ]),

    async handleSubmit () {
      await this.verifyOtp({
        otp: this.otp,
        hashPass: this.hashPass
      })

      if (this.token) {
        this.$beepAxios.defaults.headers.Authorization = this.token
        this.$cookies.set('sm_token', this.token)

        await this.updateIsLoggedIn(true)
        await this.getUserMobileNumber(this.token) // TODO: check if failure needs to be handled on UI
        this.navigateToLoggedInRoute()
      }
    },

    handleReset () { // done
      this.otpShade = false
      this.otpModal = false

      this.$store.commit('resetState')
      this.$emit('reset')
    },

    navigateToLoggedInRoute () {
      this.$router.push({ name: this.fromName || 'MyOrders', query: this.$route.query })
    },

    async handleResendOTP () { // done
      if (!this.resendTimer) {
        await this.resendOTP(this.recipient)
        this.resetResendOtpTimer()
        document.querySelector('#otp').focus()
      }
    },

    handleClear () { // done
      this.clearOtpInput(this.otp)
    },

    hideOtpModal () { // done
      this.otpShade = false
      this.otpModal = false
    },

    openUrl (url) {
      window.open(url, '_blank')
    },

    resetResendOtpTimer () {
      this.resendTimer = parseInt(process.env.VUE_APP_RESENT_OTP_TIMER, 10)
    }
  }
}
</script>

<style scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  /* display: none; <- Crashes Chrome on hover */
  -webkit-appearance: none;
  margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
}

input[type='number'] {
  -moz-appearance: textfield; /* Firefox */
}
</style>
